<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { debounce } from '@chatwoot/utils';

import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['update:modelValue', 'change']);

const { t } = useI18n();
const agents = useMapGetter('agents/getVerifiedAgents');
const teams = useMapGetter('teams/getTeams');
const labels = useMapGetter('labels/getLabels');

const search = ref(props.modelValue.search || '');
const responsibleAgentId = ref(props.modelValue.responsibleAgentId || '');
const teamId = ref(props.modelValue.teamId || '');
const labelIds = ref(props.modelValue.labelIds || []);
const assignmentStatus = ref(props.modelValue.assignmentStatus || '');

const agentOptions = computed(() => [
  { value: '', label: t('CONTACT_PORTFOLIO.FILTERS.ALL_AGENTS') },
  ...(agents.value || []).map(agent => ({
    value: String(agent.id),
    label: agent.name,
  })),
]);

const teamOptions = computed(() => [
  { value: '', label: t('CONTACT_PORTFOLIO.FILTERS.ALL_TEAMS') },
  ...(teams.value || []).map(team => ({
    value: String(team.id),
    label: team.name,
  })),
]);

const labelOptions = computed(() =>
  (labels.value || []).map(label => ({
    value: String(label.id),
    label: label.title,
    color: label.color,
  }))
);

const statusOptions = computed(() => [
  { value: '', label: t('CONTACT_PORTFOLIO.FILTERS.ALL_STATUSES') },
  {
    value: 'with_responsible',
    label: t('CONTACT_PORTFOLIO.FILTERS.WITH_RESPONSIBLE'),
  },
  {
    value: 'without_responsible',
    label: t('CONTACT_PORTFOLIO.FILTERS.WITHOUT_RESPONSIBLE'),
  },
]);

const buildFilters = () => ({
  search: search.value.trim(),
  responsibleAgentId: responsibleAgentId.value,
  teamId: teamId.value,
  labelIds: [...labelIds.value],
  assignmentStatus: assignmentStatus.value,
});

const emitChange = () => {
  const filters = buildFilters();
  emit('update:modelValue', filters);
  emit('change', filters);
};

const debouncedEmitChange = debounce(emitChange, 300);

const hasActiveFilters = computed(() => {
  const filters = buildFilters();
  return Boolean(
    filters.search ||
      filters.responsibleAgentId ||
      filters.teamId ||
      filters.labelIds.length ||
      filters.assignmentStatus
  );
});

const clearFilters = () => {
  search.value = '';
  responsibleAgentId.value = '';
  teamId.value = '';
  labelIds.value = [];
  assignmentStatus.value = '';
  emitChange();
};

watch(
  [responsibleAgentId, teamId, assignmentStatus],
  () => {
    emitChange();
  }
);

watch(labelIds, () => {
  emitChange();
}, { deep: true });

watch(search, () => {
  debouncedEmitChange();
});
</script>

<template>
  <div
    class="rounded-xl border border-n-weak bg-n-alpha-1 p-4 space-y-4"
  >
    <div class="flex items-center justify-between gap-3">
      <h3 class="text-sm font-medium text-n-slate-12">
        {{ t('CONTACT_PORTFOLIO.FILTERS.TITLE') }}
      </h3>
      <Button
        v-if="hasActiveFilters"
        sm
        faded
        slate
        :label="t('CONTACT_PORTFOLIO.FILTERS.CLEAR')"
        @click="clearFilters"
      />
    </div>

    <div class="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
      <div class="flex flex-col gap-1.5 min-w-0">
        <label class="text-xs font-medium text-n-slate-11">
          {{ t('CONTACT_PORTFOLIO.FILTERS.SEARCH') }}
        </label>
        <input
          v-model="search"
          type="text"
          class="w-full h-10 rounded-lg border border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:ring-2 focus:ring-n-brand/40 focus:border-n-brand"
          :placeholder="t('CONTACT_PORTFOLIO.FILTERS.SEARCH_PLACEHOLDER')"
        />
      </div>

      <div class="flex flex-col gap-1.5 min-w-0">
        <label class="text-xs font-medium text-n-slate-11">
          {{ t('CONTACT_PORTFOLIO.FILTERS.RESPONSIBLE_AGENT') }}
        </label>
        <ComboBox
          v-model="responsibleAgentId"
          :options="agentOptions"
          :placeholder="t('CONTACT_PORTFOLIO.FILTERS.RESPONSIBLE_AGENT_PLACEHOLDER')"
        />
      </div>

      <div class="flex flex-col gap-1.5 min-w-0">
        <label class="text-xs font-medium text-n-slate-11">
          {{ t('CONTACT_PORTFOLIO.FILTERS.DEPARTMENT') }}
        </label>
        <ComboBox
          v-model="teamId"
          :options="teamOptions"
          :placeholder="t('CONTACT_PORTFOLIO.FILTERS.DEPARTMENT_PLACEHOLDER')"
        />
      </div>

      <div class="flex flex-col gap-1.5 min-w-0">
        <label class="text-xs font-medium text-n-slate-11">
          {{ t('CONTACT_PORTFOLIO.FILTERS.STATUS') }}
        </label>
        <ComboBox
          v-model="assignmentStatus"
          :options="statusOptions"
          :placeholder="t('CONTACT_PORTFOLIO.FILTERS.STATUS_PLACEHOLDER')"
        />
      </div>

      <div class="flex flex-col gap-1.5 min-w-0 md:col-span-2 xl:col-span-2">
        <label class="text-xs font-medium text-n-slate-11">
          {{ t('CONTACT_PORTFOLIO.FILTERS.TAGS') }}
        </label>
        <TagMultiSelectComboBox
          v-model="labelIds"
          :options="labelOptions"
          :placeholder="t('CONTACT_PORTFOLIO.FILTERS.TAGS_PLACEHOLDER')"
        />
      </div>
    </div>
  </div>
</template>
